import { useId, type ReactNode } from 'react'
import styles from './RangoFeedback.module.css'

export type RangoTone = 'informative' | 'positive' | 'warning' | 'negative'

type FeedbackProps = {
  tone?: RangoTone
  title?: ReactNode
  children?: ReactNode
  /** Ação final (link ou botão) dentro da mensagem. */
  action?: ReactNode
  /** Botão de fechar — Alert apenas. */
  onDismiss?: () => void
  dismissLabel?: string
  className?: string
}

const STATUS_LABEL: Record<RangoTone, string> = {
  informative: 'Informação',
  positive: 'Sucesso',
  warning: 'Atenção',
  negative: 'Erro',
}

function StatusIcon({ tone }: { tone: RangoTone }) {
  return (
    <span className={styles.icon} data-tone={tone} aria-hidden="true">
      {tone === 'positive' ? (
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none">
          <path
            d="M5 12.5l4.5 4.5L19 7.5"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : tone === 'negative' ? (
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none">
          <path
            d="M7 7l10 10M17 7L7 17"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
          />
        </svg>
      ) : (
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none">
          <path
            d="M12 7.5v5.5"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
          />
          <circle cx="12" cy="16.8" r="1.4" fill="currentColor" />
        </svg>
      )}
    </span>
  )
}

const liveRole = (tone: RangoTone) =>
  tone === 'negative' || tone === 'warning' ? 'alert' : 'status'

/**
 * Alert Rango DS — informação sistêmica, full-width, fica no topo da tela.
 * `role` é `alert` para negativos/avisos e `status` (polite) para os demais.
 */
export function RangoAlert({
  tone = 'informative',
  title,
  children,
  action,
  onDismiss,
  dismissLabel = 'Fechar mensagem',
  className,
}: FeedbackProps) {
  const titleId = useId()
  return (
    <div
      className={[styles.alert, className].filter(Boolean).join(' ')}
      data-tone={tone}
      role={liveRole(tone)}
      aria-labelledby={title ? titleId : undefined}
      data-testid="rango-alert"
    >
      <StatusIcon tone={tone} />
      <div className={styles.content}>
        {title ? (
          <p id={titleId} className={styles.title}>
            {title}
          </p>
        ) : (
          <span className={styles.srOnly}>{STATUS_LABEL[tone]}</span>
        )}
        {children ? <div className={styles.body}>{children}</div> : null}
        {action ? <div className={styles.action}>{action}</div> : null}
      </div>
      {onDismiss ? (
        <button
          type="button"
          className={styles.dismiss}
          aria-label={dismissLabel}
          onClick={onDismiss}
        >
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden="true">
            <path
              d="M7 7l10 10M17 7L7 17"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      ) : null}
    </div>
  )
}

/**
 * Flag Rango DS — mensagem contextual no meio do conteúdo (80px mínimo,
 * título opcional, descrição de até duas linhas).
 */
export function RangoFlag({
  tone = 'informative',
  title,
  children,
  action,
  className,
}: FeedbackProps) {
  const titleId = useId()
  return (
    <div
      className={[styles.flag, className].filter(Boolean).join(' ')}
      data-tone={tone}
      role={liveRole(tone)}
      aria-labelledby={title ? titleId : undefined}
      data-testid="rango-flag"
    >
      <StatusIcon tone={tone} />
      <div className={styles.flagContent}>
        {title ? (
          <p id={titleId} className={styles.flagTitle}>
            {title}
          </p>
        ) : null}
        {children ? <p className={styles.flagBody}>{children}</p> : null}
        {action ? <div className={styles.action}>{action}</div> : null}
      </div>
    </div>
  )
}
